import './EditBillingInformation.scss';
import { Input, Modal, Select } from '@trackman/web-shared-components';
import { useGetSignUpLists } from 'queryHooks/Profile/useGetSignUpLists';
import { ReactComponent as PencilIcon } from 'images/icons/Pencil.svg';
import { useTranslation } from 'react-i18next';
import { SignUpLists } from 'types/profile';
import { useState } from 'react';
import { trimTooManySpaces } from 'utilities/inputValidation';
import { BillingInfoUpdate } from 'types/billing';
import { useUpdateBillingAddress } from 'queryHooks/Profile/Billing/useUpdateBillingAddress';

export const EditBillingInformation = ({ isOpen, close }: { isOpen: boolean; close: () => void }) => {
  const { data: signUpLists, isPending: isSignUpListsPending } = useGetSignUpLists();

  const updateBillingInfo = useUpdateBillingAddress({ onSuccess: () => close() });

  const confirm = (billingInfo: BillingInfoUpdate) => updateBillingInfo.mutate(billingInfo);

  return !isSignUpListsPending ? (
    <EditBillingAddressModal
      isOpen={isOpen}
      signUpLists={signUpLists}
      isSaving={updateBillingInfo.isPending}
      close={close}
      confirm={confirm}
    />
  ) : (
    <></>
  );
};

const EditBillingAddressModal = ({
  isOpen,
  signUpLists,
  isSaving,
  close,
  confirm,
}: {
  isOpen: boolean;
  signUpLists: SignUpLists | undefined;
  isSaving: boolean;
  close: () => void;
  confirm: (data: BillingInfoUpdate) => void;
}) => {
  const { t } = useTranslation();
  const [company, setCompany] = useState<string>('');
  const [firstName, setFirstName] = useState<string>('');
  const [lastName, setLastName] = useState<string>('');
  const [address1, setAddress1] = useState<string>('');
  const [address2, setAddress2] = useState<string>('');
  const [postalCode, setPostalCode] = useState<string>('');
  const [city, setCity] = useState<string>('');
  const [country, setCountry] = useState<string>('');
  const [state, setState] = useState<string>('');

  const isFormValid = company && firstName && lastName && address1 && postalCode && city && country && (country === 'US' ? state : true);

  const payload = (): BillingInfoUpdate => ({
    Address1: address1,
    Address2: address2,
    City: city,
    Company: company,
    Country: country,
    FirstName: firstName,
    LastName: lastName,
    PostalCode: postalCode,
    State: state,
  });

  const handleConfirm = () => {
    confirm(payload());

    setCompany('');
    setFirstName('');
    setLastName('');
    setAddress1('');
    setAddress2('');
    setPostalCode('');
    setCity('');
    setCountry('');
    setState('');
  };

  return (
    <Modal
      open={isOpen}
      onClose={() => close()}
      title={t('EditBillingInformation', { ns: 'billingInformation' })}
      titleIcon={<PencilIcon />}
      className='edit-payment-method-modal'
      cancelBtn={{
        label: t('Cancel', { ns: 'general' }),
        onClick: () => close(),
        className: 'styled-button secondary',
      }}
      confirmBtn={{
        label: t('SaveChanges', { ns: 'editProfile' }),
        onClick: () => handleConfirm(),
        disabled: isSaving || !isFormValid,
      }}
    >
      <div className='modal-body'>
        <div className='profile-section-title profile-section-title--secondary'>{t('BillingDetails', { ns: 'billingInformation' })}</div>
        <Input
          label={t('CompanyWithAsterisk', { ns: 'billingInformation' })}
          value={company ?? ''}
          hint=' '
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => setCompany(trimTooManySpaces(e.currentTarget.value))}
        />
        <Input
          label={t('FirstNameWithAsterisk', { ns: 'billingInformation' })}
          value={firstName ?? ''}
          hint=' '
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => setFirstName(trimTooManySpaces(e.currentTarget.value))}
        />
        <Input
          label={t('LastNameWithAsterisk', { ns: 'billingInformation' })}
          value={lastName ?? ''}
          hint=' '
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => setLastName(trimTooManySpaces(e.currentTarget.value))}
        />
        <Input
          label={t('StreetName1WithAsterisk', { ns: 'billingInformation' })}
          value={address1 ?? ''}
          hint=' '
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => setAddress1(trimTooManySpaces(e.currentTarget.value))}
        />
        <Input
          label={t('StreetName2', { ns: 'editProfile' })}
          value={address2 ?? ''}
          hint=' '
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => setAddress2(trimTooManySpaces(e.target.value))}
        />
        <Input
          label={t('PostalCodeWithAsterisk', { ns: 'billingInformation' })}
          value={postalCode ?? ''}
          hint=' '
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => setPostalCode(trimTooManySpaces(e.currentTarget.value))}
        />
        <Input
          label={t('CityWithAsterisk', { ns: 'billingInformation' })}
          value={city ?? ''}
          hint=' '
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => setCity(trimTooManySpaces(e.currentTarget.value))}
        />
        <Select
          label={t('CountryWithAsterisk', { ns: 'billingInformation' })}
          value={country ?? ''}
          hint=' '
          onChange={(event) => setCountry(trimTooManySpaces(event.target.value as string))}
          selectOptions={signUpLists?.Countries.map((country) => {
            return {
              label: country.name === ' ' ? t('PleaseSelectACountry', { ns: 'editProfile' }) : country.name,
              value: country.twoLetterIsoCode,
            };
          })}
        />
        {country === 'US' && (
          <Select
            label={t('StateWithAsterisk', { ns: 'billingInformation' })}
            value={state ?? ''}
            hint=' '
            onChange={(event) => setState(trimTooManySpaces(event.target.value as string))}
            selectOptions={signUpLists?.States.map((state) => {
              return {
                label: state === ' ' ? t('PleaseSelectAState', { ns: 'editProfile' }) : state,
                value: state,
              };
            })}
          />
        )}
      </div>
    </Modal>
  );
};
